/**
 * Medical reference data — Stanford Emergency Manual (EPIC) scenarios.
 *
 * Source: Stanford Anesthesia Cognitive Aid Program, "Emergency Manual:
 * Cognitive Aids for Perioperative Crises" V4.4 (2022).
 * https://web.stanford.edu/dept/anesthesia/em/epic-manual.pdf
 *
 * Each event carries the monitor physiology (vitals / rhythm / capnogram / vent)
 * AND an `aid` array of cognitive-aid cards. When the scenario is active, the
 * instructor can push any card onto the learner monitor as an overlay.
 *
 * Shape:
 *   { name, category, description, rhythm?, capnoMorphology?, vent?, vitals,
 *     aid: [ { title, lines: string[] } ] }
 *
 * `category` groups the events into columns on the Emergency Manual tab:
 *   'acls' | 'airway' | 'circulation' | 'metabolic' | 'equipment'
 *
 * NOTE: content is condensed for teaching use — always defer to the full manual
 * and institutional protocols for actual clinical care.
 */
export const EPIC_SCENARIOS = {
  // ── ACLS ──────────────────────────────────────────────────────────────────
  epicAsystolePEA: {
    name: 'Asystole / PEA',
    // High-quality CPR + epinephrine q3–5 min; treat reversible causes (H's & T's).
    correctInterventions: ['epinephrine', 'ivFluid'],
    category: 'acls',
    description: 'No pulse AND non-shockable rhythm (asystole or any non-VF/VT).',
    rhythm: 'asystole',
    vitals: { HR: 0, SpO2: 40, NIBP_Sys: 0, NIBP_Dia: 0, ABP_Sys: 0, ABP_Dia: 0, EtCO2: 0, RR: 0, Temp: 36.0, CVP: 0 },
    aid: [
      { title: 'Immediate', lines: [
        'Inform team · identify leader · call a code · call for code cart',
        'CPR 100–120/min, depth ≥5 cm, full recoil; minimize breaks',
        'Keep EtCO₂ > 10 mmHg and diastolic BP > 20 mmHg',
        'Rotate compressors with rhythm check every 2 min',
        'Place defib pads — if becomes shockable → VF/VT algorithm',
      ] },
      { title: 'Airway / Meds', lines: [
        '100% O₂ 10–15 L/min; if mask: 30 compressions : 2 breaths',
        'If airway secured: 10 breaths/min, TV 6–7 mL/kg',
        'Turn off volatile anesthetic and vasodilating drips',
        'Epinephrine 1 mg IV push every 3–5 min',
        'Ensure functional IV or IO access',
      ] },
      { title: "H's & T's", lines: [
        'Hypovolemia, Hypoxemia, H⁺ (acidosis), Hypo/Hyperkalemia',
        'Hypoglycemia, Hypocalcemia, Hypo/Hyperthermia',
        'Toxins (anesthetic OD, LAST, med error)',
        'Tamponade, Tension pneumothorax',
        'Thrombosis — coronary (MI) or pulmonary (PE)',
      ] },
    ],
  },

  epicBradycardia: {
    name: 'Bradycardia — Unstable',
    // Atropine first; if refractory → transcutaneous pacing and/or epinephrine or
    // dopamine infusion.
    correctInterventions: ['atropine', 'epinephrine', 'dopamine', 'transcutaneousPacemaker'],
    category: 'acls',
    description: 'Pulse present, HR < 50 bpm, inadequate perfusion.',
    vitals: { HR: 38, SpO2: 96, NIBP_Sys: 84, NIBP_Dia: 48, ABP_Sys: 82, ABP_Dia: 46, EtCO2: 34, RR: 12, Temp: 36.6, CVP: 5 },
    aid: [
      { title: 'Immediate', lines: [
        'Inform team; call for help. If no pulse → CPR / Asystole–PEA',
        '100% O₂; confirm adequate ventilation & oxygenation',
        'Stop vagal stimuli: desufflate abdomen, remove retractors,',
        'release pressure on eyes/neck, drain bladder',
        'Consider decreasing anesthetic / analgesic depth',
      ] },
      { title: 'Medications', lines: [
        'Atropine 0.5–1 mg IV q3min, repeat to max 3 mg',
        'If atropine ineffective: epinephrine 5–10 mcg IV',
        'Dopamine infusion 5–20 mcg/kg/min, or',
        'Epinephrine infusion 0.02–0.3 mcg/kg/min',
        'If stable: glycopyrrolate 0.2–0.4 mg IV',
      ] },
      { title: 'Pacing', lines: [
        'Place defib pads; transcutaneous / transvenous pacing',
        'Set pacer rate ≥ 80 bpm',
        'Increase mA until electrical capture; confirm pulse',
        'Set output 10 mA above mechanical capture',
        'Consult ICU / Cardiology; send ABG, Hgb, lytes, troponin',
      ] },
    ],
  },

  epicSVT: {
    name: 'SVT — Unstable',
    category: 'acls',
    description: 'Non-compensatory tachycardia, pulse present, often > 150 or sudden onset. Unstable → synchronised cardioversion; adenosine only if regular narrow-complex and not delaying the shock.',
    // Unstable → synchronised cardioversion first; adenosine is a non-delaying adjunct.
    correctInterventions: ['synchronizedCardioversion', 'adenosine'],
    rhythm: 'svt',
    vitals: { HR: 190, SpO2: 92, NIBP_Sys: 72, NIBP_Dia: 46, ABP_Sys: 70, ABP_Dia: 44, EtCO2: 30, RR: 22, Temp: 37.0, CVP: 7 },
    aid: [
      { title: 'Assess Stability', lines: [
        'Unstable if ANY: SBP < 75, sudden SBP drop below baseline,',
        'acute ischemia / chest pain, acute CHF, altered mental status',
        '100% O₂; place defib pads for possible cardioversion',
        'If stable: rule out sinus tachycardia, treat by rhythm',
      ] },
      { title: 'Unstable → Cardiovert', lines: [
        'Sedate if not anesthetized. Settings by QRS & rhythm:',
        'Narrow + regular: sync 50–100 J biphasic',
        'Narrow + irregular: sync 120–200 J biphasic',
        'Wide + regular: sync 100 J biphasic',
        'Wide + irregular: UNSYNC 200 J biphasic',
      ] },
      { title: 'Stable — Meds', lines: [
        'Narrow+regular: adenosine 6 mg IV push+flush, then 12 mg',
        '(avoid adenosine in WPW or asthma)',
        'Rate control: esmolol, metoprolol, or diltiazem',
        'Wide+regular likely VT: amiodarone 150 mg IV over 10 min',
        'Wide+irregular → likely polymorphic VT: Cardiology STAT',
      ] },
    ],
  },

  epicVFVT: {
    name: 'VF / Pulseless VT',
    // Defibrillate + CPR; epinephrine q3–5 min; amiodarone (or lidocaine) for
    // refractory VF/VT; magnesium for torsades.
    correctInterventions: ['epinephrine', 'amiodarone', 'lidocaine', 'magnesium'],
    category: 'acls',
    description: 'No pulse AND VF or VT — shockable.',
    rhythm: 'vf',
    vitals: { HR: 0, SpO2: 40, NIBP_Sys: 0, NIBP_Dia: 0, ABP_Sys: 0, ABP_Dia: 0, EtCO2: 0, RR: 0, Temp: 36.0, CVP: 0 },
    aid: [
      { title: 'Immediate', lines: [
        'Inform team · call a code · call for code cart',
        'CPR 100–120/min, depth ≥5 cm, full recoil',
        'Defibrillation is higher priority than intubation',
        'Place pads and defibrillate: 120–200 J biphasic',
        'Resume CPR; increase energy, repeat shock every 2 min',
      ] },
      { title: 'Medications', lines: [
        'Turn off volatile anesthetic and vasodilating drips',
        'After 2nd shock: epinephrine 1 mg IV q3–5 min',
        'After 3rd shock: amiodarone 300 mg IV push',
        '(or lidocaine 1–1.5 mg/kg); redose amiodarone 150 mg',
        'Torsades/hypoMg: magnesium 1–2 g IV',
      ] },
      { title: "H's & T's", lines: [
        'Hypovolemia, Hypoxemia, H⁺, Hypo/Hyperkalemia',
        'Hypothermia, Toxins (LAST, anesthetic OD)',
        'Tamponade, Tension pneumothorax',
        'Thrombosis — coronary or pulmonary',
        'Consider ECMO / CPB if refractory',
      ] },
    ],
  },

  // ── Airway / Respiratory ────────────────────────────────────────────────────
  epicAnaphylaxis: {
    name: 'Anaphylaxis',
    category: 'airway',
    description: 'Severe hypotension, bronchospasm, high inspiratory pressure, angioedema, flushing/hives (or none).',
    capnoMorphology: 'obstructed',
    vent: { vte: 320, ppeak: 50, pplat: 24 },
    vitals: { HR: 148, SpO2: 86, NIBP_Sys: 58, NIBP_Dia: 30, ABP_Sys: 55, ABP_Dia: 28, EtCO2: 60, RR: 30, Temp: 37.5, CVP: 1 },
    aid: [
      { title: 'Primary Treatment', lines: [
        '100% O₂; secure airway (early intubation if angioedema)',
        'Epinephrine 10–100 mcg IV (no IV: 500 mcg IM);',
        'increase dose q2min until improvement — may need > 1 mg',
        'Start early epinephrine infusion',
        'Rapid IV fluid bolus — may require several liters; legs up',
        'Remove allergens (abx, NMB, chlorhexidine, latex, contrast…)',
      ] },
      { title: 'Bronchospasm', lines: [
        'If unable to ventilate: epi 5–10 mcg IV, ketamine 10–50 mg IV,',
        'or magnesium 1–2 g IV',
        'If able: albuterol 4–8 puffs / 2.5 mg neb; sevo to 1 MAC',
        'H1 diphenhydramine 25–50 mg IV; H2 famotidine 20 mg IV',
        'Steroid: hydrocortisone 100 mg or methylprednisolone 125 mg',
      ] },
      { title: 'Rule Out / Follow-up', lines: [
        'Rule out: LAST, aspiration, embolism, hemorrhage, MI,',
        'pneumothorax, sepsis, distributive/obstructive shock',
        'Send peak serum tryptase 1–2 hr after onset',
        'Monitor ≥ 6 hr (ICU 12–24 hr if severe — biphasic risk)',
        'Add allergen to chart; refer for allergy testing',
      ] },
    ],
  },

  epicBronchospasm: {
    name: 'Bronchospasm',
    category: 'airway',
    description: 'Inability to ventilate, high peak pressure, wheeze, ↑EtCO₂ with upsloping waveform, ↓TV.',
    capnoMorphology: 'obstructed',
    vent: { vte: 360, ppeak: 45, pplat: 22 },
    vitals: { HR: 112, SpO2: 90, NIBP_Sys: 128, NIBP_Dia: 82, ABP_Sys: 126, ABP_Dia: 80, EtCO2: 60, RR: 26, Temp: 37.1, CVP: 6 },
    aid: [
      { title: 'Early Actions', lines: [
        'If hypotensive, suspect air-trapping: briefly disconnect circuit',
        'If hypotension + tachycardia + rash → treat as Anaphylaxis',
        '100% O₂; if stridor/hypoxemia consider intubation',
        'Optimize exhalation: I:E 1:3 or 1:4, minimize PEEP (0–5),',
        'avoid hyperinflation (goal TV 6 mL/kg)',
      ] },
      { title: 'Deepen / Check', lines: [
        'Bolus propofol; increase sevoflurane or isoflurane',
        'Consider additional neuromuscular blockade',
        'Check CO₂ waveform confirms airway placement',
        'Auscultate for endobronchial intubation',
        'Soft-suction ETT for kink / mucous plug',
      ] },
      { title: 'Medications', lines: [
        'If severe: epinephrine 5–10 mcg IV q3–5 min (or 200 mcg subq)',
        'Add glycopyrrolate 0.2–0.4 mg IV; watch for tachycardia',
        'If able to ventilate: albuterol 4–8 puffs / 2.5 mg neb ± ipratropium',
        'Consider ketamine 10–50 mg IV, magnesium 1–2 g IV,',
        'or hydrocortisone 100 mg IV',
      ] },
    ],
  },

  epicDelayedEmergence: {
    name: 'Delayed Emergence',
    category: 'airway',
    description: 'Patient less responsive than expected during emergence; abnormal postop neuro exam.',
    vitals: { HR: 62, SpO2: 95, NIBP_Sys: 118, NIBP_Dia: 74, ABP_Sys: 116, ABP_Dia: 72, EtCO2: 48, RR: 6, Temp: 35.8, CVP: 4 },
    aid: [
      { title: 'First Steps', lines: [
        'Confirm all volatile and IV anesthetics are OFF',
        'Correct hypoxemia, hypercarbia, hypothermia, hypotension',
        'Check for high ICP: widened pulse pressure, bradycardia, irregular RR',
        'Reverse residual paralysis (sugammadex / neostigmine+glyco)',
        'Perform neuro exam: pupils, motor symmetry, gag',
      ] },
      { title: 'Reversal Agents', lines: [
        'Opioid: naloxone 40 mcg IV, may double & repeat q2min (max 400 mcg)',
        'Benzo: flumazenil 0.2 mg IV q1min, max 1 mg',
        'Anticholinergic syndrome: physostigmine 1 mg IV (atropine ready)',
        'Reversal agents are short-acting — re-treat as needed',
        'Correct hypoglycemia; send ABG + electrolytes',
      ] },
      { title: 'Rare Causes', lines: [
        'Stroke → Stroke Code, STAT head CT, Neuro consult',
        'High spinal, seizure, serotonin syndrome',
        'Malignant hyperthermia, myxedema coma, thyroid storm',
        'Hepatic / uremic encephalopathy; delayed drug clearance',
        'Medication error',
      ] },
    ],
  },

  epicDifficultAirway: {
    name: 'Difficult Airway / Cric',
    category: 'airway',
    description: 'Cannot Intubate, Cannot Oxygenate (CICO) — priority is front-of-neck access.',
    vent: { vte: 120, ppeak: 40, pplat: 20 },
    vitals: { HR: 132, SpO2: 68, NIBP_Sys: 105, NIBP_Dia: 66, ABP_Sys: 103, ABP_Dia: 64, EtCO2: 20, RR: 0, Temp: 37.0, CVP: 6 },
    aid: [
      { title: 'Can Oxygenate', lines: [
        'Monitor CO₂ and SpO₂; if cannot oxygenate at any time → cric',
        'Options while oxygenating:',
        '· Awaken patient · finish with SGA/LMA or mask',
        '· Intubate through SGA/LMA · combined video/fiberoptic',
        'Call for cric-capable help early',
      ] },
      { title: 'CICO → eFONA', lines: [
        'Priority is cutting the neck. Get cric kit: scalpel, bougie, 6.0 ETT',
        '1. Extend neck, laryngeal handshake to find midline',
        '2. 8 cm vertical midline skin incision',
        '3–5. Stab cricothyroid membrane, rotate blade to feet, pull',
        '6–8. Bougie → 6.0 ETT → inflate cuff, ventilate, confirm CO₂',
      ] },
      { title: 'Prevention', lines: [
        'Difficult-airway cart, 2nd-gen SGA/LMA, fiberoptic in room',
        'Consider awake intubation',
        'High-flow apneic oxygenation',
        'Video laryngoscopy as first attempt',
        'ENT / surgery in room; consider awake trach',
      ] },
    ],
  },

  epicHypoxemia: {
    name: 'Hypoxemia',
    // 100% O₂, confirm ventilation/recruit, intubate if failing; treat bronchospasm.
    correctInterventions: ['nonRebreather', 'intubation', 'albuterol'],
    category: 'airway',
    description: 'Low SpO₂/PaO₂ refractory to intervention or unclear cause.',
    vitals: { HR: 118, SpO2: 82, NIBP_Sys: 112, NIBP_Dia: 72, ABP_Sys: 110, ABP_Dia: 70, EtCO2: 44, RR: 24, Temp: 36.9, CVP: 5 },
    aid: [
      { title: 'Check Monitors', lines: [
        '100% O₂ 10–15 L/min',
        'Gas analyzer: rule out low FiO₂ or high N₂O (→ O₂ Failure)',
        'SpO₂ waveform, probe position, limb perfusion',
        'CO₂ waveform, look for circuit disconnect',
        'Manually ventilate to check compliance',
      ] },
      { title: 'ETT & Recruit', lines: [
        'Check ETT position; auscultate breath sounds',
        'Soft-suction ETT for secretions/patency',
        'Recruitment breaths; consider PEEP (caution if hypotensive)',
        'Head up, desufflate abdomen to increase FRC',
        'If wheezing: albuterol; if severe epi 5–10 mcg IV',
      ] },
      { title: 'V/Q Differential', lines: [
        'Shunt (less O₂-responsive): atelectasis, aspiration, edema,',
        'endobronchial intubation, mucus plug, one-lung ventilation',
        'Rare/critical: anaphylaxis, embolism, pneumothorax, RV failure',
        'Hemoglobinopathy: carboxy-/met-hemoglobin (send co-oximetry)',
        'Advanced: bronchoscopy, lung US, STAT CXR, consider ECMO',
      ] },
    ],
  },

  epicHighAirwayPressure: {
    name: 'High Airway Pressure',
    category: 'airway',
    description: 'Peak pressure > 5 cmH₂O above baseline or > 35 cmH₂O.',
    vent: { vte: 300, ppeak: 50, pplat: 30 },
    vitals: { HR: 104, SpO2: 92, NIBP_Sys: 108, NIBP_Dia: 68, ABP_Sys: 106, ABP_Dia: 66, EtCO2: 55, RR: 20, Temp: 37.0, CVP: 8 },
    aid: [
      { title: 'Localize', lines: [
        '100% O₂; confirm CO₂ present; inspect circuit valves/connections',
        'Disconnect circuit from ETT to rule out air-trapping (auto-PEEP)',
        'Squeeze reservoir bag during disconnect:',
        '· High pressure → machine/circuit obstructed → switch to Ambu',
        '· Low/zero → problem is ETT or lungs → reconnect machine',
      ] },
      { title: 'Optimize / Check ETT', lines: [
        'Increase anesthetic (propofol 20 mg IV); add NMB',
        'Release abdominal insufflation; reassess position (head-up/supine)',
        'Pass soft suction catheter — rule out kink / mucous plug',
        'If unable to clear: replace ETT; consider bronchoscopy',
        'Adjust APL valve when manually ventilating',
      ] },
      { title: 'Causes', lines: [
        'Asymmetric sounds: endobronchial intubation, pneumothorax',
        'Bronchospasm, laryngospasm, light anesthesia, chest rigidity',
        'Aspiration, mucous plug, airway tumor/foreign body',
        'Pulmonary edema, pleural effusion',
        'Machine: circuit obstruction, closed scavenger, valve malfunction',
      ] },
    ],
  },

  // ── Circulation / Cardiac ───────────────────────────────────────────────────
  epicHypotension: {
    name: 'Hypotension',
    category: 'circulation',
    description: 'Low BP refractory to intervention or unclear cause.',
    vitals: { HR: 122, SpO2: 94, NIBP_Sys: 70, NIBP_Dia: 40, ABP_Sys: 68, ABP_Dia: 38, EtCO2: 30, RR: 22, Temp: 36.6, CVP: 3 },
    aid: [
      { title: 'Early Actions', lines: [
        'Check vitals & EtCO₂; if no pulse/abnormal rhythm → ACLS',
        'Check arterial line transducer height; cycle NIBP',
        'Inspect surgical field for hemorrhage / pressure on great vessels',
        'Functional IV/IO; rapid crystalloid or colloid bolus; legs up',
        'Turn anesthetic down or off',
      ] },
      { title: 'Medications', lines: [
        'Ephedrine 5–20 mg IV or phenylephrine 100–300 mcg IV',
        'If ineffective: epinephrine 10–50 mcg IV ± vasopressin 0.5–1 U IV',
        'Calcium chloride 1 g (or gluconate 1–3 g)',
        'Adrenal insufficiency: hydrocortisone 100 mg IV',
        'Vasoplegia: methylene blue 1.5–2 mg/kg over 20–60 min',
      ] },
      { title: 'Differential (BP=SVR×CO)', lines: [
        'Low SVR: anaphylaxis, sepsis, neuraxial/high spinal, vasodilators',
        'Low preload: hemorrhage, embolism, tamponade, pneumothorax,',
        'auto-PEEP, IVC compression',
        'Low inotropy: ischemia, LAST, acidosis, hypoxemia',
        'Consider TEE/TTE; rule out rapidly lethal causes',
      ] },
    ],
  },

  epicHypertension: {
    name: 'Hypertension',
    category: 'circulation',
    description: 'High systolic/diastolic BP refractory to initial intervention.',
    vitals: { HR: 92, SpO2: 96, NIBP_Sys: 190, NIBP_Dia: 110, ABP_Sys: 192, ABP_Dia: 112, EtCO2: 38, RR: 16, Temp: 36.9, CVP: 7 },
    aid: [
      { title: 'First Steps', lines: [
        'Check arterial-line transducer position; consider zeroing',
        'Check NIBP cuff position / cable compression',
        'Treat reversible causes before antihypertensives:',
        'surgical stimulus, tourniquet, full bladder, hypercarbia,',
        'light anesthesia / empty vaporizer, med error, recent epinephrine',
      ] },
      { title: 'Medications', lines: [
        'Labetalol 5–10 mg IV, wait 5 min to redose',
        'Hydralazine 2–5 mg IV, wait 15 min',
        'Nitroglycerin 20–50 mcg IV, wait 3 min',
        'Nitroprusside 20–50 mcg IV, wait 3 min',
        'Infusion: clevidipine 1–5 mg/hr',
      ] },
      { title: 'Rare Causes', lines: [
        'Autonomic hyperreflexia (cord injury above T6)',
        'Ischemia (ST changes) → Myocardial Ischemia',
        'Malignant hyperthermia, neuroleptic malignant syndrome',
        'Pheochromocytoma, preeclampsia, raised ICP',
        'Serotonin syndrome, thyroid storm, stroke',
      ] },
    ],
  },

  epicMyocardialIschemia: {
    name: 'Myocardial Ischemia',
    category: 'circulation',
    description: 'ST elevation/depression, T-wave inversion, arrhythmia, wall-motion abnormality, chest pain.',
    rhythm: 'st_elevation',
    vitals: { HR: 92, SpO2: 95, NIBP_Sys: 100, NIBP_Dia: 64, ABP_Sys: 98, ABP_Dia: 62, EtCO2: 34, RR: 18, Temp: 36.7, CVP: 8 },
    aid: [
      { title: 'First Steps', lines: [
        'Supplemental O₂ to keep SpO₂ ≥ 95%',
        '12-lead ECG; verify lead position; expand to II, V5',
        'Apply defibrillator pads and leads',
        'Pause/stop procedure if possible',
        'Treat tachy/brady/hypo/hypertension',
      ] },
      { title: 'Medications', lines: [
        'Discuss antiplatelet/anticoag risk with surgeon:',
        'Aspirin 160–325 mg PO/NG/PR; P2Y12 inhibitor; heparin',
        'Treat pain: fentanyl or morphine',
        'Nitroglycerin paste/infusion (avoid if hypotensive)',
        'Esmolol to slow rate (avoid if brady/block/hypotensive)',
      ] },
      { title: 'Escalation', lines: [
        'STEMI → Cardiology for emergent revascularization / cath lab',
        'If acute pulmonary edema: furosemide 10–40 mg IV',
        'Arterial line; send troponin, ABG, lytes, coags',
        'TEE/TTE for wall motion & valve function',
        'Consider ECMO / CPB / IABP',
      ] },
    ],
  },

  epicHemorrhage: {
    name: 'Hemorrhage',
    category: 'circulation',
    description: 'Increased suction/sponge use, tachycardia, hypotension.',
    vitals: { HR: 142, SpO2: 92, NIBP_Sys: 72, NIBP_Dia: 42, ABP_Sys: 70, ABP_Dia: 40, EtCO2: 26, RR: 30, Temp: 35.8, CVP: 1 },
    aid: [
      { title: 'Early Response', lines: [
        'Call Anesthesia/Surgery/Nursing help; activate MTP',
        'IV fluid bolus; if significant loss prioritize blood products',
        'Large-bore IV; consider IO or central access',
        'Temporize severe hypotension with vasopressor bolus',
        '100% O₂; intubate before airway swelling; legs up',
      ] },
      { title: 'Surgical / Setup', lines: [
        'Tell surgeon; suggest packing, vessel compression/clamp,',
        'hemostatic agents (thrombin, fibrin glue)',
        'Get more surgical help (Trauma/Vascular/Cardiac)',
        'Set up rapid infuser and cell saver',
        'Warm room, warm fluids, forced air; place arterial line + foley',
      ] },
      { title: 'Transfusion', lines: [
        'If significant bleeding: transfuse, don\'t wait for labs',
        'Ratio 1–2 PRBC : 1 FFP : 1 platelet pack',
        'PRBC if Hgb < 7–10; FFP if INR/PTT > 1.5×',
        'Platelets if < 50–100K; cryo if fibrinogen < 80–100',
        'TXA 1 g IV over 10 min then 1 g over 8 hr; maintain calcium',
      ] },
    ],
  },

  epicPulmonaryEmbolism: {
    name: 'Embolism — Pulmonary',
    category: 'circulation',
    description: 'Sudden ↓EtCO₂/BP/SpO₂, ↑CVP; risk in ortho, pregnancy, cancer, laparoscopy.',
    vitals: { HR: 132, SpO2: 88, NIBP_Sys: 78, NIBP_Dia: 48, ABP_Sys: 76, ABP_Dia: 46, EtCO2: 18, RR: 26, Temp: 36.8, CVP: 14 },
    aid: [
      { title: 'Immediate', lines: [
        'Inform team; get code cart; consider terminating procedure',
        'If no pulse → CPR, check rhythm, follow ACLS',
        '100% O₂; turn off volatile & vasodilating drips',
        'IV vasopressor bolus to support circulation',
        'Consider rapid fluid bolus (caution — RV overload)',
      ] },
      { title: 'Support RV', lines: [
        'If RV dysfunction on TEE/TTE: maintain sinus rhythm,',
        'normal RV volume, RV contractility; decrease RV afterload',
        'Avoid hypoxemia, hypercarbia, acidosis',
        '→ see Right Heart Failure',
        'Severe decompensation: consider ECMO / CPB',
      ] },
      { title: 'By Embolism Type', lines: [
        'Thrombus: rtPA alteplase 10 mg IV then 90 mg over 2 hr, or thrombectomy',
        'Air/CO₂: stop entrainment, flood field with saline, aspirate CVL,',
        'L lateral decubitus; consider hyperbaric O₂',
        'Amniotic fluid: supportive ABC, treat DIC/seizures, deliver fetus',
        'Fat/cement: supportive care',
      ] },
    ],
  },

  epicRightHeartFailure: {
    name: 'Right Heart Failure',
    category: 'circulation',
    description: 'Hypotension, RV strain on ECG, dilated/poor RV on echo, septal flattening.',
    vitals: { HR: 104, SpO2: 88, NIBP_Sys: 84, NIBP_Dia: 54, ABP_Sys: 82, ABP_Dia: 52, EtCO2: 28, RR: 22, Temp: 36.9, CVP: 20 },
    aid: [
      { title: 'Immediate', lines: [
        'Call for pulmonary vasodilator (nitric oxide / epoprostenol)',
        'Get code cart and TEE/TTE; call Cardiac Anes / Cardiology',
        '100% O₂',
        'Decrease TV, increase RR to lower intrathoracic pressure',
        'Minimize PEEP if tolerated; avoid hypoxemia',
      ] },
      { title: 'Decrease Afterload', lines: [
        'Offload RV — lower PVR:',
        'Inhaled prostacyclin, inhaled nitric oxide, or IV pulm vasodilator',
        'Avoid hypoxemia, hypercarbia, acidosis, high airway pressure',
        'Refractory hypotension: consider IABP',
      ] },
      { title: 'Contractility / Volume', lines: [
        'Avoid hypotension (maintain coronary perfusion)',
        '↓ RV contractility → epinephrine',
        'Normal contractility → vasopressin or norepinephrine',
        'RV overload (CVP > 20, SvO₂ < 65%): diurese',
        'Maintain sinus rhythm & atrial kick; rule out tamponade, PE, PTX, RV MI',
      ] },
    ],
  },

  epicHighSpinal: {
    name: 'High / Total Spinal',
    category: 'circulation',
    description: 'Block higher/faster than expected: arm weakness, dyspnea/apnea, bradycardia, hypotension, LOC.',
    // Support airway (intubate if apnoeic) + circulation: vasopressors, atropine for
    // bradycardia, IV fluids.
    correctInterventions: ['intubation', 'ephedrine', 'phenylephrine', 'epinephrine', 'atropine', 'ivFluid'],
    vitals: { HR: 38, SpO2: 62, NIBP_Sys: 48, NIBP_Dia: 28, ABP_Sys: 46, ABP_Dia: 26, EtCO2: 20, RR: 2, Temp: 37.0, CVP: 2 },
    aid: [
      { title: 'Airway / Circulation', lines: [
        'If no pulse → CPR / ACLS',
        '100% O₂; support ventilation, intubate (may last hours)',
        'Patient may be conscious — reassure, give midazolam (amnesia)',
        'Severe brady/hypotension: epinephrine 10–100 mcg IV, escalate',
        'Mild brady: atropine 0.5–1 mg / glyco — but move to epi quickly',
      ] },
      { title: 'Preload', lines: [
        'Rapid IV bolus with pressure bag — may need several liters',
        'Raise both legs to increase preload',
        'Maintain neutral position (head-down raises spinal level)',
        'Phenylephrine unlikely to be effective',
        'If LAST possible: give 20% lipid emulsion rapidly',
      ] },
      { title: 'Pregnancy', lines: [
        'Ensure left uterine displacement',
        'Call OB and Neonatology',
        'Prepare for emergent / perimortem Cesarean',
        'Monitor fetal heart tones',
      ] },
    ],
  },

  epicTransfusionReaction: {
    name: 'Transfusion Reaction',
    category: 'circulation',
    description: 'Fever, flank/back pain, hypotension, dark urine, urticaria, wheeze, oozing/DIC.',
    // STOP the transfusion. Support BP (fluids, epinephrine/pressors if anaphylactic),
    // antihistamine + steroid, and maintain urine output (fluids ± furosemide) in haemolysis.
    correctInterventions: ['ivFluid', 'epinephrine', 'antihistamine', 'corticosteroid', 'furosemide'],
    vitals: { HR: 122, SpO2: 92, NIBP_Sys: 80, NIBP_Dia: 48, ABP_Sys: 78, ABP_Dia: 46, EtCO2: 34, RR: 22, Temp: 39.2, CVP: 6 },
    aid: [
      { title: 'Immediate', lines: [
        'STOP the transfusion; retain the blood product bag(s)',
        'Inform team; call for help / code cart',
        '100% O₂; consider intubation if none in place',
        'IV fluid bolus',
        'Send labs and return units per local protocol',
      ] },
      { title: 'By Reaction Type', lines: [
        'Hemolytic: watch for DIC; maintain urine output (fluids, diuretics)',
        'Febrile: acetaminophen 1000 mg IV; rule out hemolysis & contamination',
        'Anaphylactic: epinephrine bolus then infusion → see Anaphylaxis',
        'Steroid: dexamethasone 4–8 mg or hydrocortisone 100 mg IV',
        'Antihistamine: diphenhydramine 25–50 mg + famotidine 20 mg IV',
      ] },
    ],
  },

  epicTrauma: {
    name: 'Trauma',
    category: 'circulation',
    description: 'Blunt or penetrating injury to major organs.',
    vitals: { HR: 132, SpO2: 90, NIBP_Sys: 80, NIBP_Dia: 48, ABP_Sys: 78, ABP_Dia: 46, EtCO2: 30, RR: 26, Temp: 35.6, CVP: 2 },
    aid: [
      { title: 'On Arrival', lines: [
        'Call trauma code; activate trauma OR and MTP',
        'All state name & role; closed-loop communication',
        'If no pulse: ACLS while transfusing + primary survey',
        'Standard monitors, large-bore IV; maintain c-spine precautions',
      ] },
      { title: 'Primary Survey (ABCDE)', lines: [
        'A/B: airway, oxygenation; RSI w/ video laryngoscopy + in-line stab.',
        'If hypotensive, reduce/omit induction meds; chest tube for PTX/hemothorax',
        'C: control external hemorrhage; transfusion > crystalloid; do FAST',
        'D: level of consciousness, pupils, glucose, GCS',
        'E: fully expose then cover to prevent hypothermia',
      ] },
      { title: 'Secondary Survey', lines: [
        'AMPLE: Allergies, Meds, Past history, Last meal, Events',
        'AT MIST: Age, Time, Mechanism, Injuries, Systemic review, Treatments',
        'Head-to-toe exam + radiologic evaluation',
      ] },
    ],
  },

  // ── Metabolic ───────────────────────────────────────────────────────────────
  epicMalignantHyperthermia: {
    name: 'Malignant Hyperthermia',
    category: 'metabolic',
    description: 'Mixed acidosis, ↑EtCO₂/HR/RR, hyperthermia, masseter spasm, rigidity without shivering.',
    vitals: { HR: 132, SpO2: 94, NIBP_Sys: 138, NIBP_Dia: 84, ABP_Sys: 136, ABP_Dia: 82, EtCO2: 65, RR: 30, Temp: 40.0, CVP: 7 },
    // Definitive: dantrolene. Supportive: treat hyperkalaemia (calcium, insulin+D50,
    // bicarb, salbutamol) and arrhythmia (amiodarone). Avoid triggers (volatile,
    // succinylcholine) and calcium-channel blockers (CCB + dantrolene → hyperK/collapse).
    correctInterventions: ['dantrolene', 'calciumChloride', 'insulinD50', 'sodiumBicarb', 'albuterol', 'amiodarone'],
    contraindicated: ['sevoflurane', 'desflurane', 'isoflurane', 'succinylcholine', 'diltiazem'],
    aid: [
      { title: 'Stop Triggers', lines: [
        'Get MH cart with dantrolene; call for help',
        'Stop volatile anesthetic and succinylcholine',
        'Do NOT change machine or circuit',
        '100% O₂ 10–15 L/min; add charcoal filters if available',
        'Maximize minute ventilation (avoid air-trapping)',
      ] },
      { title: 'Dantrolene', lines: [
        'Initial dantrolene 2.5 mg/kg IV',
        'Ryanodex: 250 mg vial in 5 mL sterile water (70 kg → 175 mg)',
        'Dantrium/Revonto: 20 mg per 60 mL (70 kg → 9 vials!)',
        'Repeat 2.5 mg/kg q5min until hypercarbia & rigidity resolve',
        'May need > 10 mg/kg. Non-triggering maintenance (propofol)',
      ] },
      { title: 'Supportive', lines: [
        'Hyperkalemia: CaCl 10 mg/kg (max 2 g), insulin+D50, albuterol, bicarb',
        'Arrhythmia: amiodarone / esmolol / Mg — avoid CCB & Na-channel blockers',
        'If T > 38°C: cold IV fluid 20–30 mL/kg, ice packs, stop warming',
        'Send ABG, K⁺, CK, urine myoglobin, coags; foley (UO 1–2 mL/kg/hr)',
        'MH Hotline 1-800-644-9737 (MHAUS)',
      ] },
    ],
  },

  epicLAST: {
    name: 'Local Anesthetic Toxicity',
    category: 'metabolic',
    description: 'Neuro (seizure, AMS, tinnitus, perioral numbness) + cardiac (collapse, arrhythmia).',
    rhythm: 'vtach',
    vitals: { HR: 160, SpO2: 72, NIBP_Sys: 60, NIBP_Dia: 35, ABP_Sys: 58, ABP_Dia: 33, EtCO2: 22, RR: 6, Temp: 36.8, CVP: 3 },
    // Same ASRA/NYSORA management as the periop `last` scenario (see scenarios.js).
    correctInterventions: ['intralipid', 'midazolam', 'amiodarone', 'epinephrine', 'nonRebreather', 'intubation'],
    contraindicated: ['lidocaine', 'esmolol', 'labetalol', 'diltiazem', 'vasopressin', 'propofol'],
    aid: [
      { title: 'Immediate', lines: [
        'Call for help; call for 20% lipid emulsion STAT',
        'Stop any local anesthetic injection/infusion',
        '100% O₂; if pulseless start CPR (may be prolonged ± ECMO)',
        'If unstable: early call for ECMO / CPB',
      ] },
      { title: 'Lipid Emulsion 20%', lines: [
        'Bolus 100 mL IV over 2–3 min (< 70 kg: 1.5 mL/kg)',
        'Infuse 250 mL over 15–20 min (< 70 kg: 0.25 mL/kg/min ×20 min)',
        'If unstable: repeat bolus & double infusion until stable',
        'Max dose 12 mL/kg; continue ≥ 15 min after stability',
      ] },
      { title: 'Seizure / Arrhythmia', lines: [
        'Seizure: midazolam 2–4 mg IV (or propofol 20 mg if BP stable)',
        'Arrhythmia: amiodarone 150 mg IV slow — avoid CCB/BB/lidocaine',
        'Hypotension: LOW-dose epinephrine 0.2–1 mcg/kg IV; avoid vasopressin',
        'Arrest: defibrillate VF/VT; low-dose epi; amiodarone 300 mg',
        'Monitor ≥ 2 hr post-seizure, 6 hr post-instability, 24–48 hr post-arrest',
      ] },
    ],
  },

  epicPneumothorax: {
    name: 'Pneumothorax',
    // Tension → immediate needle/finger decompression, then chest drain; 100% O₂.
    correctInterventions: ['needleDecompression', 'nonRebreather', 'intubation'],
    category: 'metabolic',
    description: '↑PIP, tachycardia, hypotension/hypoxemia, decreased/asymmetric sounds, ↑CVP, tracheal deviation (late).',
    vent: { vte: 240, ppeak: 45, pplat: 24 },
    vitals: { HR: 130, SpO2: 82, NIBP_Sys: 84, NIBP_Dia: 54, ABP_Sys: 82, ABP_Dia: 52, EtCO2: 30, RR: 22, Temp: 37.0, CVP: 12 },
    aid: [
      { title: 'Fast Checks', lines: [
        '100% O₂; call for help, get code cart',
        'Exclude endobronchial intubation (sounds, ETT depth)',
        'Exclude ETT obstruction (pass soft suction catheter)',
        'Exclude auto-PEEP (briefly disconnect circuit)',
      ] },
      { title: 'Decompress', lines: [
        'If unstable and no chest tube available:',
        '14–16 g IV catheter, 4th/5th intercostal space,',
        'anterior-to-mid-axillary line (may hear whoosh)',
        'Leave catheter in place until chest tube placed',
        'Have appropriate personnel place chest tube',
      ] },
      { title: 'Confirm', lines: [
        'Lung US: lung sliding (normal) vs lung point (PTX);',
        'seashore (normal) vs barcode (PTX)',
        'Consider STAT portable CXR — but do NOT delay treatment',
        'Higher incidence: trauma, COPD, cardiothoracic, upper abdominal',
        'ICU care for chest-tube management',
      ] },
    ],
  },

  // ── Equipment / Environment ─────────────────────────────────────────────────
  epicOxygenFailure: {
    name: 'Oxygen Failure',
    category: 'equipment',
    description: 'O₂ failure alarm, inappropriately low FiO₂ on gas analyzer, flow meter reads low.',
    vitals: { HR: 108, SpO2: 85, NIBP_Sys: 118, NIBP_Dia: 74, ABP_Sys: 116, ABP_Dia: 72, EtCO2: 40, RR: 14, Temp: 36.8, CVP: 5 },
    aid: [
      { title: 'Ventilate Off-Machine', lines: [
        'Inform team; get code cart with O₂ cylinder',
        'Disconnect patient; ventilate with self-inflating (Ambu) bag',
        'Do NOT connect Ambu to machine aux O₂ (same faulty source)',
        'Assign a capable person to manual ventilation',
        'If no pulse → CPR',
      ] },
      { title: 'Non-Machine O₂', lines: [
        'Attach Ambu to transport O₂ e-cylinder (or room air 21%)',
        'Connect gas sampling line between patient and Ambu',
        'Verify airway placement with CO₂',
        'Verify FiO₂ on gas analyzer (100% cylinder, 21% room air)',
        'Maintain anesthesia with IV meds; turn off volatile; lowest FGF',
      ] },
      { title: 'Report', lines: [
        'Inform Charge Nurse, Anesthesia Lead, all ORs',
        'Contact bio-engineering — report & get help',
        'Find out if issue is system-wide',
        'Discuss plan for this patient and OR schedule',
      ] },
    ],
  },

  epicPowerFailure: {
    name: 'Power Failure',
    category: 'equipment',
    description: 'Sudden darkness, loss of electrically powered equipment.',
    vitals: { HR: 88, SpO2: 97, NIBP_Sys: 120, NIBP_Dia: 78, ABP_Sys: 118, ABP_Dia: 76, EtCO2: 38, RR: 12, Temp: 36.8, CVP: 5 },
    aid: [
      { title: 'Light & Ventilate', lines: [
        'Inform team; call for help',
        'Light source: laryngoscope, cell phone, flashlight, open door',
        'Ventilator may have ~30 min battery; consider transport vent',
        'If not working: spontaneous ventilation or Ambu bag',
        'Convert to TIVA with battery pump or dial-a-flow',
      ] },
      { title: 'Monitors & Power', lines: [
        'If monitors fail: assign continuous pulse check;',
        'manual BP; use transport monitor or defibrillator monitor',
        'If O₂ supply/alarms affected → see Oxygen Failure',
        'Connect life-sustaining equipment to emergency (red) outlets',
        'Disconnect non-essential equipment from emergency outlets',
      ] },
      { title: 'Report', lines: [
        'Inform Charge Nurse, Anesthesia Lead, all ORs',
        'Call bio-engineering; if isolated, check tripped breaker',
        'Find out if issue is system-wide',
        'Discuss implications for patient and OR schedule',
      ] },
    ],
  },

  epicAirwayFire: {
    name: 'Fire — Airway',
    category: 'equipment',
    description: 'Sudden pop, spark, flame, smoke, heat, or odor from the airway.',
    vitals: { HR: 105, SpO2: 93, NIBP_Sys: 132, NIBP_Dia: 82, ABP_Sys: 130, ABP_Dia: 80, EtCO2: 40, RR: 14, Temp: 37.0, CVP: 5 },
    aid: [
      { title: 'Immediate Response', lines: [
        'Anesthesia: disconnect breathing circuit (prevents torch);',
        'stop fresh gas flow',
        'Surgeon: clamp/kink ETT, then remove ETT + airway foreign bodies',
        'Pour saline into airway and suction debris',
        'If fire spreads → Fire — Non-Airway',
      ] },
      { title: 'After Extinguished', lines: [
        'Re-establish oxygenation; minimize FiO₂ (consider air)',
        'Reintubate with ETT ≥ 7.0 mm before swelling',
        'Ensure anesthesia (propofol infusion)',
        'Bronchoscopy of entire airway; inspect ETT pieces',
        'Save materials; dexamethasone 8 mg IV; ICU care',
      ] },
      { title: 'Prevention', lines: [
        'Fire = fuel + oxidizer + ignition',
        'Avoid FiO₂ > 0.3 and N₂O',
        'Laser cases: laser-resistant ETT, cuff below cords, wet gauze',
        'Surgeon keeps laser on standby; confirm FiO₂ < 0.3 before use',
        'Communicate all FiO₂ changes',
      ] },
    ],
  },

  epicORFire: {
    name: 'Fire — Non-Airway',
    category: 'equipment',
    description: 'Sudden pop, spark, flame, smoke, heat, or odor (drapes/patient).',
    vitals: { HR: 100, SpO2: 96, NIBP_Sys: 128, NIBP_Dia: 80, ABP_Sys: 126, ABP_Dia: 78, EtCO2: 38, RR: 14, Temp: 37.0, CVP: 5 },
    aid: [
      { title: 'Immediate', lines: [
        'Inform team; activate fire alarm / call code red; get CO₂ extinguisher',
        'Stop fresh gas flow; disconnect circuit; ventilate on room air (Ambu)',
        'Remove all burning/flammable materials off patient onto floor',
        'Extinguish flames on the patient',
      ] },
      { title: 'PASS', lines: [
        'Non-electrical fire: CO₂ extinguisher + saline/water',
        'Electrical fire: CO₂ extinguisher ONLY',
        'PULL the pin · AIM at base of fire',
        'SQUEEZE trigger in 5-sec bursts · SWEEP side to side',
        'Ensure anesthesia (propofol); assess injuries; monitor vitals',
      ] },
      { title: 'Contain / Evacuate', lines: [
        'Close OR doors; turn off external O₂ and N₂O valves',
        'If continued smoke/fire: evacuate patient and staff',
        'If no smoke/fire: shelter in place',
        'If fire spreads to airway → Fire — Airway',
        'Recap with surgeon and OR leadership',
      ] },
    ],
  },
};

// Attach investigation result cards (physical exam / labs / imaging) to each
// EPIC scenario. Kept in a separate file so the management cognitive aids and
// the "what the student finds when they investigate" content stay decoupled.
import { EPIC_INVESTIGATIONS } from './epicInvestigations.js';

for (const [key, list] of Object.entries(EPIC_INVESTIGATIONS)) {
  if (EPIC_SCENARIOS[key]) EPIC_SCENARIOS[key].investigations = list;
}
